<template>
  <div v-if="loading" :class="[$style.wrapper, 'mt-loader-wrapper']">
    <animation-processing fill="new-blue" :class="$style.pageSpinner" data-qa-id="loading-icon" />
    <span data-qa-id="loading-text" class="mt-serif mt-serif--size3">
      {{ $t('pages.contract.spinner.assigned_foundries') }}
    </span>
  </div>
  <div v-else :class="$style.bundleMainWrapper">
    <!-- Foundry Email Verify Banner for grace period expired-->
    <FouEmailVerify
      :showCloseIcon="false"
      :onlyGracePeriodExpired="true"
      labelKey="components.general.verify_email.banner.grace_period_expired_label"
    />
    <div
      class="form-sec form-sec--bundle-header"
      data-qa-id="create-bundle-form"
      :class="[getImpersonateUser ? 'form-sec--shift-bundle-header' : '']"
    >
      <div class="form-sec__content is-flex is-flex--align-center is-flex--justify-between">
        <div class="form-sec__content-left is-flex is-flex--align-center">
          <button class="mt-button mt-button--back is-ghost" data-qa-id="back-button" @click="goBack()">
            <back-icon />
          </button>
          <h2 class="mt-e-title" data-qa-id="create-new-bundle-title">
            <span v-if="isBundleNew">{{ $t('pages.bundle.sub_header.create_new_bundle') }}</span>
            <span v-else>{{ $t('pages.bundle.sub_header.edit_bundle') }}</span>
          </h2>
        </div>
        <div class="form-sec__content-right">
          <div class="is-flex is-flex--align-center is-flex--justify-end">
            <div v-if="savingDraft">
              <button class="mt-button is-ghost" data-qa-id="saving-draft-text">
                {{ $t('pages.bundle.draft.saving_text') }}
              </button>
            </div>
            <div v-if="!isBundleLive" class="mt-button-group is-flex">
              <button data-qa-id="draft" @click.prevent="isConfirmDiscardModalOpen = true">
                {{ $t('pages.bundle.draft.discard') }}
              </button>
            </div>
            <div>  
              <button
                :class="[
                  'mt-button is-small',
                  isPublishButtonActive && !pricceError && !bannedWordForDescription && !bannedWord
                    ? 'mt-button--blue'
                    : 'mt-button--gray'
                ]"
                data-qa-id="publish"
                @click.prevent="validateAndOpenConfirm()"
                :disabled="!isPublishButtonActive || pricceError || bannedWordForDescription || bannedWord"
              >
                {{ $t('pages.bundle.draft.publish') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bundle Information Section -->
    <div class="form-sec form-sec--bundle-info">
      <h3 class="mt-e-title" data-qa-id="basic-information">
        {{ $t('pages.bundle.draft.information') }}
      </h3>
      <p class="mt-e-para" data-qa-id="basic-max-info">
        {{ $t('pages.bundle.draft.max_info') }}
      </p>
      <div class="is-flex">
        <div class="flex-1 fou-input-col fou-input-col-errorClass">
          <div>
            <div
              class="fou-bundle-input-wrap"
              :class="[
                { 'mt-input--error': Object.hasOwn(errors, 'bundle name') || !isBundleNameUnique || bannedWord },
                'mt-input--banned-word'
              ]"
            >
              <TextField
                id="bundle-name"
                v-model="bundleFormData.name"
                placeholder=" "
                data-qa-id="bundle-name"
                rules="bundle_name_required|min:3|custom_max_char_no_html:100|script_tag_validate|alpha_num_space"
                name="bundle name"
                :input-class="[{ 'mt-input--error': Object.hasOwn(errors, 'bundle name') || !isBundleNameUnique }]"
                :show-error-message="false"
                @blur="updateBundleName()"
                :moduleName="this.$route.name"
                :foundryId="sessionFoundryId"
                :description="bundleFormData.name"
                @update-banned-word="updateBannedWord"
                :show-banned-word-error-message="false"
                :show-warning-word-error-message="false"
                @update-warning-existence="updateWarningWord"
                @update-warning-words="listOfWarningWords"
              />
              <label class="fou-bundle-input-placeholder" for="bundle-name">
                {{ $t('pages.bundle.draft.name') }}
              </label>
            </div>
            <div
              v-if="Object.hasOwn(errors, 'bundle name')"
              class="ml-0 mt-error"
              :class="[{ 'mt-input-banned-word-exist': bannedWord }]"
              data-qa-id="bundle-name-error"
            >
              {{ errors['bundle name'] }}
            </div>
            <p
              v-else-if="!isBundleNameUnique && !Object.hasOwn(errors, 'bundle name')"
              class="mt-error"
              data-qa-id="bundle-name-unique-error"
            >
              {{ $t('pages.bundle.draft.errors.unique_name') }}
            </p>
            <p v-if="bannedWord" class="mt-error bundleInput-banned-error">
              {{ $t('pages.profile.foundry_profile.banned_word_error_message') }}
            </p>
            <p v-if="warningWord" class="mt-warning bundleInput-banned-error">
              {{
                $t('pages.profile.foundry_profile.warning_word_error_message', { words: warningWordList.join('", "') })
              }}
            </p>
          </div>
        </div>

        <div class="flex-1 fou-input-col">
          <div
            class="fou-bundle-input-wrap fou-bundle-input-wrap--select"
            :class="[
              { 'mt-input--error': Object.hasOwn(errors, 'foundries') },
              { 'is-disabled': isBundleLive },
              { 'is-aligned': foundriesOptions?.length === 1 }
            ]"
          >
            <FouSelect
              v-if="foundriesOptions?.length > 1"
              id="foundries"
              ref="foundrySelectRef"
              v-model="bundleFormData.foundries"
              name="foundries"
              class="foundryInput"
              type="checkbox"
              data-qa-id="foundries"
              placeholder=" "
              :options="foundriesOptions"
              :initial-value="bundleFormData.foundries"
              :reduce="op => op"
              deselect-from-dropdown
              :close-on-select="false"
              is-mulitple-select-with-no-search
              tags-outside
              disable-auto-complete
              multiple
              :is-error="Boolean(foundriesErrors?.length)"
              :show-label="true"
              label-class="fou-bundle-input-placeholder fou-select-label"
              label-name="Foundry"
              :disabled="isBundleLive"
              @option:selected="onFoundriesSelected"
              @option:deselected="onFoundriesDeselected"
              @search:blur="onFoundriesBlur()"
            />
            <template v-if="foundriesOptions?.length === 1">
              {{ foundriesOptions[0].label }}
            </template>
          </div>
          <div v-if="foundriesErrors?.length">
            <p v-for="(error, i) in foundriesErrors" :key="i" class="ml-0 mt-error" data-qa-id="foundries-error">
              {{ error }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Bundle Description Section -->
    <div class="form-sec form-sec--bundle-description">
      <h3 class="mt-e-title" data-qa-id="bundle-description">
        {{ $t('pages.bundle.draft.description') }}
      </h3>
      <p class="mt-e-para" data-qa-id="bundle-description-info">
        {{ $t('pages.bundle.draft.description_info') }}
      </p>
      
      <div class="is-flex is-flex--align-center">
        <div class="flex-1 fou-input-col">
          <div class="fou-bundle-texteditor-wrap">
            <AiTextEditor
              :config="aiTextEditorConfig"
              :foundryId="sessionFoundryId"
              :initialValue="bundleFormData.description[0].description"
              @on-change-html="handleBundleDescriptionHtmlChange"
              @on-blur-validation-done="updateBundleDescription"
              @on-banned-word-update="updateBannedWordDesc"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Add Fonts Section -->
    <div class="form-sec form-sec--font-select">
      <div class="form-sec__content is-flex is-flex--justify-between is-flex--align-start">
        <div v-if="isBundleLive" class="form-sec__content-left">
          <h3 class="mt-e-title" data-qa-id="select-fonts-title">
            {{ $t('pages.bundle.published.select_fonts_title') }}
          </h3>
          <div class="mt-e-para">
            <p data-qa-id="select-fonts-description">
              {{ $t('pages.bundle.published.select_fonts_description') }}
            </p>
            <TextField
              id="bundle-fonts"
              v-model="bundleInfo.collection"
              type="hidden"
              name="bundle fonts"
              rules="min_bundle_fonts:2"
              emit-blur
              :error-class="['mt-error--bundle-font']"
            />
          </div>
        </div>
        <div v-else class="form-sec__content-left">
          <h3 class="mt-e-title" data-qa-id="select-fonts-title">
            {{ $t('pages.bundle.draft.select_fonts_title') }}
          </h3>
          <div class="mt-e-para">
            <p data-qa-id="select-fonts-description">
              {{ $t('pages.bundle.draft.select_fonts_description') }}
            </p>
            <TextField
              id="bundle-fonts"
              v-model="bundleInfo.collection"
              type="hidden"
              name="bundle fonts"
              rules="min_bundle_fonts:2"
              emit-blur
              :error-class="['mt-error--bundle-font']"
            />
          </div>
        </div>
        <div class="form-sec__content-right">
          <button
            :class="[
              'mt-button mt-button--blue is-small is-bordered is-flex is-flex--align-center',
              { 'is-disabled': isBundleLive }
            ]"
            type="button"
            data-qa-id="add-fonts"
            :disabled="isBundleLive"
            @click.prevent="validateFoundriesAndOpenSideBar"
          >
            <PlusIcon :path-fill="iconDisabledColor" />
            {{ $t('pages.bundle.draft.add_fonts') }}
          </button>
        </div>
      </div>

      <div v-if="bundleInfo?.collection?.length" class="form-sec__draggable-wrap">
        <draggable
          v-model="bundleInfo.collection"
          :disabled="true"
          element="ul"
          class="mt-group"
          item-key="id"
          group="font-sort"
          @end="updateFontSort"
        >
          <template #item="{ element: sku }">
            <template v-if="!isEmpty(sku.styles)">
              <div
                :key="sku.styles[0]?.styleId"
                :class="[
                  'is-flex is-flex--align-center',
                  isBundleLive ? 'fou-fonts-add-section-disable__content' : 'fou-fonts-add-section__content'
                ]"
              >
                <div
                  class="fou-fonts-add-section__content__skuname is-flex is-flex--align-center is-flex--justify-between"
                >
                  <div data-qa-id="font-style" class="fou-fonts-add-section__previewer-wrapper">
                    <FontPreviewer
                      :src="getFontRenderSrc({ md5: sku.styles[0]?.md5Checksum, name: sku.styles[0]?.styleName })"
                      :family-name="sku.styles[0]?.styleName"
                    />
                  </div>
                  <div
                    :class="['fou-fonts-add-section__content__vendorname', { 'is-disabled': isBundleLive }]"
                    data-qa-id="font-vendor-name"
                  >
                    {{ sku.vendorName }}
                  </div>
                </div>
                <div data-qa-id="delete-btn" class="fou-fonts-add-section__content__deleteicon">
                  <button class="mt-button is-small is-ghost" :disabled="isBundleLive" @click="removeFont(sku.styleId)">
                    <DeleteIcon :width="24" :height="24" :path-fill="fontDeleteDisableColor" />
                  </button>
                </div>
              </div>
            </template>
          </template>
        </draggable>
      </div>
    </div>

    <!-- Part Number Section -->
    <div class="form-sec form-sec--part-number">
      <h3 class="mt-e-title part_no_title" data-qa-id="bundle-part-number">
        {{ $t('pages.bundle.draft.part_number') }}
        <span class="optional_span">
          {{ $t('pages.bundle.draft.optional_text') }}
        </span>
      </h3>
      <p class="mt-e-para" data-qa-id="bundle-part-number-info">
        {{ $t('pages.bundle.draft.custom_part_num') }}
      </p>
      <div class="is-flex is-flex--align-center">
        <div class="flex-1 fou-input-col">
          <part-number
            v-model="bundleFormData.partNumber"
            data-qa-id="part-number-txt-fld"
            :errors="errors"
            @blur="v => updatePartNumber(v)"
          />
        </div>
      </div>
    </div>

    <div class="images-main-div">
      <div class="images-inner-div">
        <!-- Bundle image Section -->
        <ImageUploader
          data-qa-id-title="bundle-main-image-title"
          :title="$t('pages.bundle.draft.uploader.image')"
          :description="$t('pages.bundle.draft.uploader.description')"
          :otherDescription="$t('pages.bundle.draft.uploader.lower_screen_description')"
          uploader-id="image-uploader"
          thumbnail-id="image-thumbnail"
          :errors="errors"
          rules="atleast_three_image|custom_rule:3,15"
          :imageUploadStatus="getBundleMainImageScanStatus"
          :avCheckFalseFiles="getBundleMainImagesWithFalseAvCheck"
          name="bundle image"
          criteria="uploader"
          :images="bundleImages"
          data-qa-id="bundle-image"
          :drag-text="$t('pages.bundle.draft.uploader.drag_text')"
          :updateImagesSortOrder="updateBundleSubmission"
          @update:thingsToKeepModal="showThingstoKeepModal"
        />

        <!-- Inspirational gallery Section -->
        <ImageUploader
          data-qa-id-title="bundle-inspiration-gallery-title"
          :title="$t('pages.bundle.draft.inspirational.title')"
          :showOptionalText="true"
          :description="$t('pages.bundle.draft.inspirational.description')"
          uploader-id="inspirational-uploader"
          thumbnail-id="inspirational-thumbnail"
          rules="bundle_image:0,15"
          :errors="errors"
          name="inspiration image"
          :imageUploadStatus="getBundleInspirationImageScanStatus"
          :avCheckFalseFiles="getBundleInspirationImagesWithFalseAvCheck"
          criteria="inspirational"
          :priority="2"
          :images="inspirationalImages"
          data-qa-id="inspirational-image"
          :drag-text="$t('pages.bundle.draft.inspirational.drag_text')"
          :updateImagesSortOrder="updateBundleSubmission"
          @update:thingsToKeepModal="showThingstoKeepModal"
        />
      </div>

      <!-- Bundle Image Info Section -->
      <div class="image-info-div">
        <ImageInfo
          :criteria-title="$t('pages.bundle.draft.uploader.criteria.title')"
          criteria="uploader"
          :class="isShowThingstoKeepModal ? 'showMobileModal' : 'hideMobileModal'"
          @update:thingsToKeepModal="showThingstoKeepModal"
        />
      </div>
    </div>

    <!-- Bundle Price Section -->
    <BundlePrice
      ref="bundlePriceRef"
      v-model="bundleFormData.prices"
      :bundle-form-data="bundleFormData"
      :fonts-count="bundleInfo?.collection?.length"
      @update:model-value="updateBundlePriceModelValue"
      @validate="validateAndSubmitBundlePrice"
      @priceError="priceErrorUpdate"
    />
  </div>

  <AddFontsSideBar
    v-if="isSideBarInitialLoaded"
    v-show="isAddFontsSideBarOpen"
    ref="addFontsSideBarRef"
    :is-add-fonts-side-bar-open="isAddFontsSideBarOpen"
    @close="isAddFontsSideBarOpen = false"
    @update:bundle="onUpdateFonts"
  />

  <DeleteFoundryModal
    modalId="foundry-remove-modal"
    :isModalOpen="isFoundryRemoveModalOpen"
    :saving-draft="savingDraft"
    @closeModal="isFoundryRemoveModalOpen = false"
    @confirm="confirmRemovingFoundryAndFont(existedFoundryFont.foundryId, existedFoundryFont.skuId)"
  />

  <!-- Preview Draft -->
  <PreviewDraft
    v-model="isPreviewDraftModalOpen"
    :bundle-preview-data="bundlePreviewData"
    :saving-draft="savingDraft"
    @click-publish="handleClickPublish"
  />
  <!-- Publish Confirmation Modal -->
  <ConfirmPublishModal
    modalId="publish-bundle-modal"
    :isModalOpen="isConfirmPublishModalOpen"
    :is-bundle-publishing="isBundlePublishing"
    @confirm="publishBundle(bundleStatus.id)"
    @closeModal="isConfirmPublishModalOpen = false"
  />

  <!-- Discard Confirmation Modal -->
  <ConfirmDiscardModal
    modalId="discard-bundle-modal"
    :isModalOpen="isConfirmDiscardModalOpen"
    :is-bundle-discarding="isBundleDiscarding"
    @confirm="discard"
    @closeModal="isConfirmDiscardModalOpen = false"
  />
</template>

<script src="./index.js"></script>

<style module lang="scss" src="../stylesheet/index.scss" />

<style lang="scss">
@import 'mpl-css/lib/assets/global/variables/fonts';
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/variables/breakpoints';
@import 'mpl-css/lib/assets/global/variables/grid';
@import 'mpl-css/lib/assets/global/variables/layout';
@import 'mpl-css/lib/assets/global/functions/index';
@import 'mpl-css/lib/assets/global/mixins/index';
@import 'mpl-css/lib/assets/global/mfpfonts/fonts.scss';

@for $i from 1 through 3 {
  .flex-#{$i} {
    flex: $i;
  }
}

.mt-loader-wrapper {
  margin: auto;
}

.is-flex {
  display: flex;

  &--align-start {
    align-items: flex-start;
  }

  &--align-center {
    align-items: center;
  }

  &--align-end {
    align-items: flex-end;
  }

  &--justify-between {
    justify-content: space-between;
  }

  &--justify-center {
    justify-content: center;
  }

  &--justify-end {
    justify-content: flex-end;
  }

  &--direction-column {
    flex-direction: column;
  }
}
.form-sec {
  padding: rem(32) rem(24);
  border-bottom: rem(1) solid map-get($colors, 'spirits--300');

  .bundlename-banned-error {
    display: none;
    @media screen and (max-width: 719px) {
      display: block;
      font-size: 0.6875rem;
      line-height: 1rem;
      margin: rem(16) 0 rem(8);
    }
  }

  .bundleInput-banned-error {
    font-size: 0.6875rem;
    line-height: 1rem;
  }

  .mt-warning {
    color: #dc5800;
    margin-top: 0.25rem;
    font-size: 13px;
  }
  .mt-e-title-container {
    margin-bottom: var(--md-spacing-unit--xs);
    margin-top: 0;
    display: flex;
    align-items: baseline;
    gap: var(--md-spacing-unit--xs);
  }

  .mt-e-title {
    margin-bottom: var(--md-spacing-unit--xs);
    margin-top: 0;
    display: flex;
    align-items: baseline;
    gap: var(--md-spacing-unit--xs);

    @media screen and (max-width: 719px) {
      font-size: rem(16);
      line-height: rem(24);
    }

    .optional_span {
      @extend .mfp-HelveticaNowMTMicro-Regular-11;
      color: map-get($colors, 'spirits--700');
    }
  }

  .part_no_title {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }

  .mt-e-para {
    color: map-get($colors, 'spirits--700');
    font-size: rem(13);
    line-height: rem(16);
    margin-bottom: rem(32);

    @media screen and (max-width: 719px) {
      margin-bottom: rem(24);
    }
  }

  .mt-button {
    border-radius: rem(8);
    min-width: 0;
    font-family: $mt-medium;
    font-weight: 500;
    white-space: nowrap;

    &:hover {
      box-shadow: none;
    }

    &.is-small {
      font-size: rem(13);
      line-height: rem(16);
      padding: 0 rem(16);
      height: rem(40);

      @media screen and (max-width: 719px) {
        &.is-small {
          font-size: rem(11);
          height: rem(32);
        }
      }
    }

    &.is-ghost {
      background-color: transparent;
      padding: 0;
      border: 0;
      color: map-get($colors, 'spirits--900');
      min-width: 0;
      font-weight: 500;
      height: auto;

      &:hover {
        background-color: transparent;
      }

      &.mt-button--blue {
        color: map-get($colors, 'blue-duck--500');

        &:hover {
          color: map-get($colors, 'blue-duck--600');
        }
      }
    }

    &.is-circle {
      border-radius: 50%;
    }

    &--black {
      min-width: 0;
      background-color: map-get($colors, 'spirits--900');
      font-weight: 500;
      border: rem(1) solid map-get($colors, 'spirits--900');

      &:hover {
        background-color: map-get($colors, 'spirits--700');
        border-color: map-get($colors, 'spirits--700');
      }
    }

    &--blue {
      min-width: 0;
      background-color: map-get($colors, 'blue-duck--500');
      font-weight: 500;
      border: rem(1) solid map-get($colors, 'blue-duck--500');

      &:hover {
        box-shadow: none;
        background-color: map-get($colors, 'blue-duck--600');
        border-color: map-get($colors, 'blue-duck--500');
      }

      &.is-bordered {
        background-color: transparent;
        color: map-get($colors, 'blue-duck--500');
        text-wrap: nowrap;

        &:hover {
          color: map-get($colors, 'blue-duck--600');
        }
      }
    }
    &--gray {
      background-color: map-get($colors, 'spirits--500');
      border: rem(1) solid map-get($colors, 'spirits--500');
    }

    &--gray-preview-btn {
      border: rem(1) solid map-get($colors, 'spirits--500');
      color: map-get($colors, 'spirits--500');
      background-color: map-get($colors, 'white');
    }

    &--white {
      border: rem(1) solid map-get($colors, 'white');
      background-color: map-get($colors, 'white');
    }
  }

  &--bundle-header {
    padding-top: rem(24);
    padding-bottom: rem(24);
    top: rem(56);
    right: 0;
    left: rem(304);
    z-index: 4;
    background-color: map-get($colors, 'white');

    @media screen and (max-width: 1279px) {
      left: 0;
    }

    .form-sec__content {
      &-left {
        gap: rem(16);

        .mt-e-title {
          font-size: rem(23);
          line-height: rem(32);
          margin: 0;

          @media screen and (max-width: 719px) {
            font-size: rem(19);
            line-height: rem(24);
          }
        }

        .mt-button--back {
          .mt-icon {
            height: rem(16);
          }
        }
      }

      &-right {
        @media screen and (max-width: 719px) {
          position: fixed;
          right: 0;
          bottom: 0;
          left: 0;
          background-color: map-get($colors, 'white');
          padding: rem(16) rem(24);
          box-shadow: 0 0 10px rgba($color: map-get($colors, 'black'), $alpha: 0.15);
          z-index: 3;

          .mt-button {
            height: rem(32);
            font-size: rem(11);
          }
        }

        > .is-flex {
          gap: rem(16);
        }

        .mt-button-group {
          gap: rem(8);
        }
      }
    }
  }
  &--shift-bundle-header {
    top: rem(104);
  }

  &--bundle-info {
    > .is-flex {
      gap: rem(16);

      @media screen and (max-width: 719px) {
        flex-direction: column;
        gap: rem(24);
      }
    }

    .fou-input-col {
      position: relative;

      @media screen and (max-width: 719px) {
        width: 100%;
      }

      .mt-error {
        position: absolute;
        top: 100%;
        left: 0;
        color: map-get($colors, 'red-babe--500');
        font-size: 13px;
        line-height: 1rem;
        margin-top: 4px;
      }
    }

    .fou-input-col-errorClass {
      .mt-error {
        position: unset;
      }
    }

    .fou-bundle-input-wrap--select {
      &.is-aligned {
        display: flex;
        align-items: center;
      }

      &.is-disabled {
        .foundryInput {
          .mt-select.v-select {
            .dropdown-toggle {
              .fou-select-label,
              .mt-input {
                color: map-get($colors, 'spirits--500');
              }

              .open-indicator {
                &::before {
                  opacity: 0.3;
                }
              }
            }
          }
        }
      }
    }
  }

  &--font-select {
    .form-sec__content {
      @media screen and (max-width: 719px) {
        flex-direction: column;
        gap: rem(16);
      }

      .form-sec__content-left {
        flex-basis: 88%;
        margin-right: rem(32);
        .mt-e-para {
          margin-bottom: 0;

          .mt-error-wrapper {
            margin-top: rem(4);

            @media screen and (max-width: 719px) {
              margin-top: 0;
            }

            .mt-error {
              color: map-get($colors, 'red-babe--500');
            }
          }
        }
      }

      .form-sec__content-right {
        .mt-button {
          &.is-disabled {
            color: map-get($colors, 'spirits--500');
            border-color: map-get($colors, 'spirits--500');
          }

          .mt-icon {
            margin-right: rem(8);
            width: rem(14);
            height: rem(14);
          }
        }
      }
    }

    .form-sec__draggable-wrap {
      margin-top: rem(32);

      @media screen and (max-width: 719px) {
        margin-top: rem(16);
      }

      .fou-fonts-add-section__content {
        flex-wrap: wrap;
        border: 1px solid map-get($colors, 'spirits--300');
        border-radius: rem(16);
        margin-bottom: rem(24);
        background-color: map-get($colors, 'white');
        transition: all 400ms ease-in-out;

        @media screen and (max-width: 719px) {
          margin-bottom: rem(16);
        }

        &:last-child {
          margin-bottom: 0;
        }

        &:hover {
          box-shadow: 0px 7.448114395141602px 15.162232398986816px 0px #3749510c,
            0px 25.016666412353516px 50.92678451538086px 0px #37495112, 0px 112px 228px 0px #3749511f;
        }

        &.sortable-chosen {
          border-color: map-get($colors, 'blue-duck--500');
          cursor: pointer;
          box-shadow: 0px 7.448114395141602px 15.162232398986816px 0px #3749510c,
            0px 25.016666412353516px 50.92678451538086px 0px #37495112, 0px 112px 228px 0px #3749511f;
        }

        &__dragicon {
          display: inline-flex;
          width: rem(88);
          height: rem(88);
          border-right: 1px solid map-get($colors, 'spirits--300');
          line-height: 1;
          background-color: map-get($colors, 'spirits--25');
          border-radius: 16px 0 0 16px;

          @media screen and (max-width: 1279px) {
            width: rem(48);
            height: rem(64);

            svg {
              width: rem(16);
            }
          }
        }

        &__skuname {
          width: calc(100% - 88px);
          flex-wrap: wrap;
          padding: rem(0) rem(24);

          @media screen and (max-width: 1279px) {
            width: calc(100% - 96px);
            flex-wrap: nowrap;
            gap: rem(20);
          }

          .fou-fonts-add-section__previewer-wrapper {
            display: inline-block;
            width: calc(100% - 80px);
            overflow: hidden;

            .fou-add-family-imgage-wrap {
              display: inline-block;
              width: 100%;

              img {
                width: auto;
                height: rem(56);

                @media screen and (max-width: 1279px) {
                  height: rem(32);
                }
              }
            }
          }
        }

        &__vendorname {
          color: map-get($colors, 'spirits--700');
          font-family: $mt-regular;
          font-size: rem(16);
          line-height: rem(24);

          @media screen and (max-width: 719px) {
            font-size: rem(13);
            line-height: rem(16);
          }
        }

        &__deleteicon {
          width: rem(88);
          padding: rem(31) rem(34);
          border-left: 1px solid map-get($colors, 'spirits--300');

          @media screen and (max-width: 1279px) {
            width: rem(48);
            height: rem(64);
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;

            .mt-button {
              width: rem(16);
              height: rem(16);

              svg {
                width: 100%;
                height: 100%;
                vertical-align: bottom;
              }
            }
          }
        }
      }

      .fou-fonts-add-section-disable__content {
        flex-wrap: wrap;
        border: 1px solid map-get($colors, 'spirits--300');
        border-radius: rem(16);
        margin-bottom: rem(24);
        background-color: map-get($colors, 'white');
        transition: all 400ms ease-in-out;

        @media screen and (max-width: 719px) {
          margin-bottom: rem(16);
        }

        &:last-child {
          margin-bottom: 0;
        }

        &.sortable-chosen {
          border-color: map-get($colors, 'blue-duck--500');
          cursor: pointer;
          box-shadow: 0px 7.448114395141602px 15.162232398986816px 0px #3749510c,
            0px 25.016666412353516px 50.92678451538086px 0px #37495112, 0px 112px 228px 0px #3749511f;
        }

        &__dragicon {
          display: inline-flex;
          width: rem(88);
          height: rem(88);
          border-right: 1px solid map-get($colors, 'spirits--300');
          line-height: 1;
          background-color: map-get($colors, 'spirits--25');
          border-radius: 16px 0 0 16px;

          @media screen and (max-width: 1279px) {
            width: rem(48);
            height: rem(64);

            svg {
              width: rem(16);
            }
          }
        }

        &__skuname {
          width: calc(100% - 88px);
          flex-wrap: wrap;
          padding: rem(0) rem(24);

          @media screen and (max-width: 1279px) {
            width: calc(100% - 96px);
            flex-wrap: nowrap;
            gap: rem(20);
          }

          .fou-fonts-add-section__previewer-wrapper {
            display: inline-block;
            width: calc(100% - 80px);
            overflow: hidden;

            .fou-add-family-imgage-wrap {
              display: inline-block;
              width: 100%;

              img {
                width: auto;
                height: rem(56);

                @media screen and (max-width: 1279px) {
                  height: rem(32);
                }
              }
            }
          }
        }

        &__vendorname {
          color: map-get($colors, 'spirits--700');
          font-family: $mt-regular;
          font-size: rem(16);
          line-height: rem(24);

          @media screen and (max-width: 719px) {
            font-size: rem(13);
            line-height: rem(16);
          }
        }

        &__deleteicon {
          width: rem(88);
          padding: rem(31) rem(34);
          border-left: 1px solid map-get($colors, 'spirits--300');

          @media screen and (max-width: 1279px) {
            width: rem(48);
            height: rem(64);
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;

            .mt-button {
              width: rem(16);
              height: rem(16);

              svg {
                width: 100%;
                height: 100%;
                vertical-align: bottom;
              }
            }
          }
        }
      }
    }
  }

  &--bundle-description {
    .fou-bundle-texteditor-wrap {
      position: relative;

      .fou-bundle-text {
        border-radius: 8px;
        border-left-width: 1px;
      }

      .mt-error {
        font-size: 13px;
        line-height: rem(16);
        margin-top: rem(4);
      }

      .menu-bar {
        padding: rem(0) rem(16);
        height: rem(48);
        display: flex;
        align-items: center;
      }
    }
  }

  .fou-bundle-input-wrap {
    border: 1px solid map-get($colors, 'spirits--300');
    border-radius: rem(8);
    padding: rem(8) rem(16);
    position: relative;
    height: rem(56);

    @media screen and (max-width: 719px) {
      height: rem(40);
    }

    &--select {
      padding: rem(8) rem(8) rem(8) rem(16);

      .fou-select {
        &.v-select {
          @media screen and (max-width: 719px) {
            height: rem(18);
          }

          .dropdown-toggle {
            width: 100%;
          }

          .dropdown-menu {
            width: calc(100% + 25px);
            left: rem(-15);
            border-radius: 8px;
            filter: drop-shadow(0px 25.02px 50.93px rgba(55, 73, 81, 0.12));

            .dropdown-item {
              &:hover {
                background-color: map-get($colors, 'blue-duck--50');
              }
            }
          }
        }
      }
    }

    &.mt-input--error {
      border: 1px solid map-get($colors, 'red-babe--500');

      & + .mt-error {
        font-size: 13px;
        line-height: rem(16);
        margin-top: rem(4);
      }

      & + div > .mt-error {
        font-size: 13px;
        line-height: rem(16);
        margin-top: rem(4);
      }
    }

    &.mt-input--banned-word {
      .mt-error {
        font-size: 13px;
        line-height: 1rem;
        margin-top: 0.25rem;

        @media screen and (max-width: 719px) {
          display: none;
        }
      }
    }

    .fou-bundle-input-placeholder {
      font-size: rem(16);
      line-height: rem(24);
      color: map-get($colors, 'spirits--600');
      position: absolute;
      top: rem(16);
      left: rem(16);
      z-index: 1;
      transition: 0.2s ease;

      @media screen and (max-width: 719px) {
        font-size: rem(13);
        line-height: rem(16);
        top: 50%;
        transform: translateY(-50%);
      }

      &.fou-select-label {
        margin: 0;
        left: 0;
        top: rem(8);
      }
    }

    .mt-input {
      padding: 0;
      border: none;
      width: 100%;
      margin-top: rem(16);

      @media screen and (max-width: 719px) {
        margin-top: rem(4);
        font-size: rem(13);
        line-height: rem(16);
      }

      &:focus,
      &:not(:placeholder-shown) {
        ~ .fou-bundle-input-placeholder {
          top: rem(5);
          font-size: rem(13);
          line-height: rem(16);
          transition: 0.2s ease;

          @media screen and (max-width: 719px) {
            font-size: rem(9);
            top: rem(12);
          }

          &.fou-select-label {
            top: rem(2);
          }
        }
      }
    }

    .mt-select {
      border: none;
    }
  }

  &:last-child {
    border-bottom: 0;
  }
}
.is-create-bundle-draft {
  .modal {
    background-color: rgba(0, 0, 0, 24%);
  }

  .mt-notification {
    z-index: 1010;
  }

  @media screen and (max-width: 719px) {
    & > div > iframe {
      margin-bottom: rem(80) !important;
    }
  }
}

@media screen and (max-width: map-get($breakpoints, 'xm')) {
  .is-create-bundle-draft {
    & > div > iframe {
      margin-top: rem(64) !important;
    }
  }
}

.fou-fonts-add-section__content__vendorname.is-disabled {
  color: map-get($colors, 'spirits--500') !important;
}

.images-main-div {
  display: flex;
  border-bottom: 0.0625rem solid map-get($colors, 'spirits--300');
  .images-inner-div {
    flex-grow: 1;
  }
}

.showMobileModal {
  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    display: block;
  }
}
.hideMobileModal {
  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    display: none;
  }
}
</style>

